/**
 * Settings store - user preferences state management.
 */
import { create } from 'zustand';
import { api } from '../api/client';
import type { UserSettings, UpdateSettingsData, ChatRetentionOption } from '../types';

interface SettingsState {
  settings: UserSettings | null;
  loading: boolean;
  error: string | null;
  retentionOptions: ChatRetentionOption[];

  // Actions
  fetchSettings: () => Promise<void>;
  updateSettings: (data: UpdateSettingsData) => Promise<void>;
  fetchRetentionOptions: () => Promise<void>;
}

export const useSettingsStore = create<SettingsState>((set) => ({
  settings: null,
  loading: false,
  error: null,
  retentionOptions: [
    { value: 'view_once', label: 'Delete after viewing' },
    { value: '24h', label: 'Delete after 24 hours' },
    { value: '7d', label: 'Keep for 7 days' },
    { value: 'forever', label: 'Keep forever' },
  ],

  fetchSettings: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.get<UserSettings>('/settings');
      if (response.success && response.data) {
        set({ settings: response.data, loading: false });
      } else {
        throw new Error('Failed to fetch settings');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch settings';
      set({ error: message, loading: false });
    }
  },

  updateSettings: async (data: UpdateSettingsData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.patch<UserSettings>('/settings', data);
      if (response.success && response.data) {
        set({ settings: response.data, loading: false });
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update settings';
      set({ error: message, loading: false });
      throw error;
    }
  },

  fetchRetentionOptions: async () => {
    try {
      const response = await api.get<{ options: ChatRetentionOption[] }>(
        '/settings/chat-retention-options'
      );
      if (response.success && response.data) {
        set({ retentionOptions: response.data.options });
      }
    } catch {
      // Keep default options if fetch fails
      console.warn('Failed to fetch retention options, using defaults');
    }
  },
}));
